import { useEffect } from 'react';

import { css, cx } from '@emotion/css';
import { Col, Row } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { doGetQuote } from 'src/actions';
import { QuoteDetailsDto } from 'src/dtos';
import { CurrencyField, MSpinner, MTypography, PercentageField } from 'src/lib';
import { BaseStyle, ColorConstant, FlexDirection } from 'src/styles';
import { Maybe } from 'src/utils';

const securityDetailsStyle = css`
  height: 300px;
`;

export const QuoteTextField = ({ label, value }: { label: string; value: string | number | JSX.Element }) => {
  return (
    <Row className={FlexDirection.column}>
      <MTypography variant='h3' color={ColorConstant.BRAND6} align='center'>
        {value}
      </MTypography>
      <MTypography variant='subtitle1' align='center'>
        {label}
      </MTypography>
    </Row>
  );
};

interface SecurityDetailsStatsProps {
  symbol: string;
}

export const SecurityDetailsStats = ({ symbol }: SecurityDetailsStatsProps) => {
  const dispatch = useDispatch();
  const isLoading = useSelector((state: any) => Boolean(state.quotes.details.isLoading));
  const quoteDetailsData: Maybe<QuoteDetailsDto> = useSelector((state: any) => state.quotes.details.data);

  useEffect(() => {
    if (quoteDetailsData?.symbolstring === symbol) return;

    dispatch(doGetQuote({ params: { symbol } }));
  }, [dispatch, symbol, quoteDetailsData?.symbolstring]);

  if (isLoading) {
    return <MSpinner height='300px' />;
  }

  return (
    <Row className={cx(BaseStyle.level, securityDetailsStyle)}>
      <Col span={8}>
        <QuoteTextField
          label='Day High'
          value={quoteDetailsData?.pricedata?.high ? <CurrencyField value={quoteDetailsData.pricedata.high} /> : '-'}
        />
      </Col>
      <Col span={8}>
        <QuoteTextField
          label='Change'
          value={
            quoteDetailsData?.pricedata?.change ? (
              <PercentageField
                value={quoteDetailsData.pricedata.change}
                color={quoteDetailsData.pricedata.change > 0 ? ColorConstant.GREEN6 : ColorConstant.RED6}
              />
            ) : (
              '-'
            )
          }
        />
      </Col>
      <Col span={8}>
        <QuoteTextField
          label='52 Week High'
          value={
            quoteDetailsData?.pricedata?.week52high?.content ? (
              <CurrencyField value={quoteDetailsData.pricedata.week52high.content} />
            ) : (
              '-'
            )
          }
        />
      </Col>
      <Col span={8}>
        <QuoteTextField
          label='Day Low'
          value={quoteDetailsData?.pricedata?.low ? <CurrencyField value={quoteDetailsData.pricedata.low} /> : '-'}
        />
      </Col>
      <Col span={8}>
        <QuoteTextField
          label='Bid'
          value={
            quoteDetailsData?.pricedata?.bidclose ? <CurrencyField value={quoteDetailsData.pricedata.bidclose} /> : '-'
          }
        />
      </Col>
      <Col span={8}>
        <QuoteTextField
          label='52 Week Low'
          value={
            quoteDetailsData?.pricedata?.week52low?.content ? (
              <CurrencyField value={quoteDetailsData.pricedata.week52low.content} />
            ) : (
              '-'
            )
          }
        />
      </Col>
      <Col span={8}>
        <QuoteTextField
          label='Open Price'
          value={quoteDetailsData?.pricedata?.open ? <CurrencyField value={quoteDetailsData.pricedata.open} /> : '-'}
        />
      </Col>
      <Col span={8}>
        <QuoteTextField
          label='Ask'
          value={
            quoteDetailsData?.pricedata?.askclose ? <CurrencyField value={quoteDetailsData.pricedata.askclose} /> : '-'
          }
        />
      </Col>
      <Col span={8}>
        <QuoteTextField label='Volumne' value={quoteDetailsData?.pricedata.sharevolume ?? '-'} />
      </Col>
    </Row>
  );
};
